import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMe } from '@/shared/hooks/useMe'
import { useDeprecatedSetToast } from '@/shared/hooks/useSetToast'
import type { Application, PostApplicationFields } from '@/shared/models/applicationModel'
import type { Company } from '@/shared/models/companyModel'
import {
    createApplication,
    deleteApplication,
    listApplications
} from '@/modules/applications/services/applicationsServices'

const applicationsQueryKeys = {
    root: ['applications'],
    applications: (companyId: Company['id']): string[] => [...applicationsQueryKeys.root, companyId]
}

type UseApplications = {
    enabled?: boolean
}

export const useApplications = (options?: UseApplications): UseQueryResult<Application[]> => {
    const { enabled } = options ?? {}
    const { companyId } = useMe()

    return useQuery({
        queryKey: applicationsQueryKeys.applications(companyId),
        queryFn: () => listApplications(companyId),
        enabled: enabled ?? true
    })
}

export const useCreateApplication = (
    shouldTriggerToaster = false
): UseMutationResult<Application, unknown, PostApplicationFields> => {
    const queryClient = useQueryClient()
    const { companyId } = useMe()

    const setToast = useDeprecatedSetToast()

    return useMutation({
        mutationFn: (application) => createApplication(companyId, application),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: applicationsQueryKeys.applications(companyId) })
            if (shouldTriggerToaster) {
                setToast('postApplication', 'positive')
            }
        },
        onError: () => {
            if (shouldTriggerToaster) {
                setToast('postApplication', 'negative')
            }
        }
    })
}

export const useDeleteApplication = (): UseMutationResult<Application, unknown, string> => {
    const { companyId } = useMe()
    return useMutation({
        mutationFn: (id: string) => deleteApplication(companyId, id)
    })
}

export const useInvalidateApplications = () => {
    const queryClient = useQueryClient()
    const { companyId } = useMe()

    const invalidateApplications = async () => {
        await queryClient.invalidateQueries({
            queryKey: applicationsQueryKeys.applications(companyId)
        })
    }

    return {
        invalidateApplications
    }
}
